import { ConnectionItem, Flex, Text } from "@wayflyer/flyui";
import { IconBank24Line } from "@wayflyer/flyui-icons/24/line";
import type { BankConnectionSchema, LogoLoadingState } from "bankconnect";
import { useIntl } from "react-intl";

import { BankLogoDisplay } from "../../../../components";
import { MESSAGES } from "../message-definitions";
import type { SelectedAccount } from "../utils";
import { getAccountLabel, getConnectedSubtitle, getExtractedAccountLabel, getExtractedAccountSubtitle } from "../utils";

import type { ExtractedAccountEntry } from "./extracted-account-entry";

type BankAccountsListProps = {
  bankingConnections: BankConnectionSchema[];
  extractedAccounts: ExtractedAccountEntry[];
  logoLoadingStates: LogoLoadingState;
  onSelectAccount: (selectedAccount: SelectedAccount) => void;
};

export const BankAccountsList = ({
  bankingConnections,
  extractedAccounts,
  logoLoadingStates,
  onSelectAccount,
}: BankAccountsListProps) => {
  const intl = useIntl();

  const connectionItems = bankingConnections.flatMap((connection) =>
    connection.accounts
      .filter((account) => account.pull_enabled)
      .map((account) => (
        <ConnectionItem
          key={account.id}
          label={getAccountLabel(intl, connection, account)}
          logo={<BankLogoDisplay logoState={logoLoadingStates[connection.institutionID]} />}
          subtitle={getConnectedSubtitle(intl, connection, account)}
          badgeStatus="positive"
          badgeText={intl.formatMessage(MESSAGES.readyBadge)}
          actions={[
            {
              content: intl.formatMessage(MESSAGES.manageAccountAction),
              onAction: () => onSelectAccount({ connection, account }),
            },
          ]}
        />
      ))
  );

  // no actions/detail view for extracted accounts yet - temporary pending confirmed design
  const extractedAccountItems = extractedAccounts.map(
    ({ objectKey, institutionName, businessName, account }, index) => (
      <ConnectionItem
        key={`${objectKey}-${index}-${account.accountType}-${account.currency}-${account.bankAccountMask ?? "no-mask"}`}
        label={getExtractedAccountLabel(intl, institutionName, businessName)}
        logo={<IconBank24Line />}
        subtitle={getExtractedAccountSubtitle(intl, account.accountType, account.currency, account.bankAccountMask)}
        badgeStatus="positive"
        badgeText={intl.formatMessage(MESSAGES.readyBadge)}
      />
    )
  );

  const accountItems = [...connectionItems, ...extractedAccountItems];

  if (accountItems.length === 0) {
    return null;
  }

  return (
    <Flex direction="column" gap="2" maxWidth="594px">
      <Flex gap="2">
        <Text fontWeight="semiBold">{intl.formatMessage(MESSAGES.bankAccountsSectionTitle)}</Text>
        <Text color="subtle">{accountItems.length}</Text>
      </Flex>
      <Flex direction="column" gap="2">
        {accountItems}
      </Flex>
    </Flex>
  );
};
